<template>
  <div class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
    <div class="px-5 py-3.5 border-b border-gray-100 dark:border-gray-700">
      <p class="text-sm font-semibold text-gray-900 dark:text-white">By Country</p>
      <p class="text-xs text-gray-400 dark:text-gray-500 mt-0.5">Ranked visitor counts</p>
    </div>

    <!-- Skeleton -->
    <template v-if="loading && !geoData">
      <div class="animate-pulse divide-y divide-gray-50 dark:divide-gray-800/60">
        <div v-for="i in 5" :key="i" class="flex items-center justify-between px-4 py-3 gap-3">
          <div class="flex items-center gap-2 flex-1">
            <div class="w-5 h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
            <div class="h-3 bg-gray-200 dark:bg-gray-700 rounded w-24"></div>
          </div>
          <div class="h-3 bg-gray-100 dark:bg-gray-800 rounded w-10"></div>
        </div>
      </div>
    </template>

    <!-- Empty -->
    <div v-else-if="isEmpty"
      class="flex flex-col items-center justify-center py-12 text-center px-4">
      <p class="text-sm text-gray-400 dark:text-gray-500">No country data for this period.</p>
    </div>

    <!-- Table -->
    <template v-else>
      <!-- Header -->
      <div class="flex items-center justify-between px-4 py-2.5 bg-gray-50 dark:bg-gray-800/40 border-b border-gray-100 dark:border-gray-700">
        <span class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Country</span>
        <span class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Visitors</span>
      </div>
      <div class="max-h-[340px] overflow-y-auto divide-y divide-gray-50 dark:divide-gray-800/60">
        <div v-for="(country, idx) in sorted" :key="idx">
          <!-- Row -->
          <button type="button" @click="toggle(idx)"
            class="w-full flex items-center justify-between px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors text-left">
            <div class="flex items-center gap-2 min-w-0">
              <span class="text-base leading-none flex-shrink-0">{{ flag(country.code) }}</span>
              <span class="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">{{ country.name }}</span>
            </div>
            <div class="flex items-center gap-2 flex-shrink-0 ml-3">
              <span class="text-sm font-semibold text-gray-900 dark:text-white tabular-nums">{{ country.value }}</span>
              <svg class="w-3.5 h-3.5 text-gray-300 dark:text-gray-600 transition-transform duration-150"
                :class="expanded.has(idx) ? 'rotate-90' : ''"
                fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="m9 18 6-6-6-6"/>
              </svg>
            </div>
          </button>

          <!-- Cities -->
          <div v-if="expanded.has(idx)" class="bg-gray-50/60 dark:bg-gray-800/20 divide-y divide-gray-100 dark:divide-gray-700/40">
            <div v-for="city in sortedCities(country)" :key="city.name"
              class="flex items-center justify-between px-4 py-2 pl-9">
              <div class="flex items-center gap-1.5">
                <svg class="w-3.5 h-3.5 text-gray-400 dark:text-gray-500 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"/>
                  <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z"/>
                </svg>
                <span class="text-xs text-gray-600 dark:text-gray-300">{{ city.name || 'Unknown' }}</span>
              </div>
              <span class="text-xs font-medium text-gray-600 dark:text-gray-300 tabular-nums">{{ city.value }}</span>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useAnalyticsTrafficStore } from 'stores/analyticsTraffic.js'

const props = defineProps({ duration: { type: String, required: true } })
const store = useAnalyticsTrafficStore()

const loading = computed(() => store.geoLoading)
const geoData = computed(() => store.geoData[props.duration] ?? null)
const isEmpty = computed(() => Array.isArray(geoData.value) && geoData.value.length === 0)
const sorted  = computed(() => (geoData.value ?? []).slice().sort((a, b) => b.value - a.value))

const expanded = ref(new Set())

function toggle(idx) {
  const s = new Set(expanded.value)
  s.has(idx) ? s.delete(idx) : s.add(idx)
  expanded.value = s
}

function sortedCities(country) {
  return (country.cities ?? []).slice().sort((a, b) => b.value - a.value)
}

function flag(alpha2) {
  if (!alpha2 || alpha2.length !== 2) return ''
  return [...alpha2.toUpperCase()].map(c => String.fromCodePoint(c.codePointAt(0) + 127397)).join('')
}
</script>
